import { useEffect, useState } from "react";
import { Card, Form, Offcanvas } from "react-bootstrap";
import { DashSquareFill, PlusSquareFill } from "react-bootstrap-icons";
import WebService from "../../utility/WebService";
import { toast } from "react-toastify";
import CustomButton from "../../components/CustomButton/CustomButton";

interface AddPastGlimpseProps {
  show: boolean;
  handleClose: () => void;
  data?: any;
  event?: any;
  mode?: "add" | "edit";
}

/* ✅ IMPORTANT: function that returns NEW object every time */
const createEmptyGlimpse = () => ({
  title: "",
  logo: "",
  images: [{ image: "" }],
});

const AddPastGlimpse = ({
  show,
  handleClose,
  data,
  event,
  mode = "add",
}: AddPastGlimpseProps) => {
  const [glimpses, setGlimpses] = useState<any[]>([createEmptyGlimpse()]);
  const [isLoading, setLoading] = useState(false);
  const [fileError, setFileError] = useState<string>("");

  const [validationErrors, setValidationErrors] = useState<any[]>([]);

  const validate = () => {
    const newErrors: any[] = [];

    glimpses.forEach((g, index) => {
      const err: any = {};

      if (!g.title || g.title.trim() === "") {
        err.title = "Title is required";
      }

      if (!g.logo || g.logo.trim() === "") {
        err.logo = "Logo is required";
      }

      newErrors[index] = err;
    });

    setValidationErrors(newErrors);

    // check if any error exists
    const hasError = newErrors.some((e) => e && (e.title || e.logo));
    return !hasError;
  };

  /* ---------------- RESET STATE ---------------- */
  useEffect(() => {
    if (mode === "edit" && event) {
      setGlimpses([
        {
          title: event.title || "",
          logo: event.logo || "",
          images:
            event.images && event.images.length > 0
              ? event.images.map((img: any) => ({ ...img }))
              : [{ image: "" }],
        },
      ]);
    } else {
      setGlimpses([createEmptyGlimpse()]);
    }
  }, [show, mode, event]);

  /* ---------------- FILE UPLOAD ---------------- */
  const uploadFile = (
    file: File,
    glimpseIndex: number,
    imageIndex?: number,
    type: "LOGO" | "IMAGE" = "IMAGE"
  ) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("storage", "AWARDS");
    WebService.fileUploadAPI({
      action: "upload",
      body: formData,
    }).then((res: any) => {
      const updated = [...glimpses];

      if (type === "LOGO") {
        updated[glimpseIndex].logo = res.data?.url;

        const errCopy = [...validationErrors];
        if (errCopy[glimpseIndex]) errCopy[glimpseIndex].logo = "";
        setValidationErrors(errCopy);
      } else {
        updated[glimpseIndex].images[imageIndex!].image = res.data?.url;
      }

      setGlimpses(updated);
    });
  };
  const ALLOWED_IMAGE_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/svg+xml",
  ];
  /* ---------------- SAVE ---------------- */
  const handleSave = () => {
    if (!data?._id) {
      toast.error("Please save Award Info first");
      return;
    }

    if (!validate()) {
      toast.error("Please fill required fields");
      return;
    }

    setLoading(true);

    let allGlimpses = [...(data?.past_glimpses || [])];

    if (mode === "add") {
      allGlimpses = [...allGlimpses, ...glimpses];
    }

    if (mode === "edit" && event?.index !== undefined) {
      allGlimpses[event.index] = glimpses[0];
    }

    const body = new FormData();
    body.append("past_glimpses", JSON.stringify(allGlimpses));
    body.append("id", data._id);

    WebService.fileUploadAPI({
      action: "award/addPastGlimpse",
      body,
    })
      .then(() => {
        toast.success("Past Glimpse saved");
        handleClose();
      })
      .catch(() => toast.error("Failed to save"))
      .finally(() => setLoading(false));
  };

  return (
    <Offcanvas
      show={show}
      onHide={handleClose}
      placement="end"
      className="offcanvas-custom"
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          {mode === "add" ? "Add Past Glimpse" : "Edit Past Glimpse"}
        </Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <Form>
          <Card className="p-3 bg-light my-3 position-relative">
            <span
              className="bg-white position-absolute rounded small"
              style={{ top: "-12px", left: "12px", padding: "0 8px" }}
            >
              Past Glimpses
            </span>

            {glimpses.map((item, index) => (
              <Card key={index} className="p-3 mb-3 border rounded bg-white">
                {/* TITLE */}
                <Form.Group className="mb-3">
                  <Form.Label className="label">Title <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    placeholder="Enter title"
                    value={item.title}
                    onChange={(e) => {
                      const updated = [...glimpses];
                      updated[index].title = e.target.value;
                      setGlimpses(updated);

                      const errCopy = [...validationErrors];
                      if (errCopy[index]) errCopy[index].title = "";
                      setValidationErrors(errCopy);
                    }}
                  />
                  {validationErrors[index]?.title && (
                    <p className="text-danger small mt-1">
                      {validationErrors[index].title}
                    </p>
                  )}
                </Form.Group>
                {/* LOGO */}
                <Form.Group className="mb-2">
                  <Form.Label className="label">Logo <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/png,image/jpeg,image/jpg"
                    onChange={(e) => {
                      const input = e.target as HTMLInputElement;
                      const file = input.files?.[0];

                      if (!file) return;

                      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                        const msg =
                          "Image format not supported. Please upload PNG, JPG, JPEG or SVG image.";

                        setFileError(msg);
                        toast.error(msg);
                        input.value = "";
                        return;
                      }
                      setFileError("");
                      if (file) uploadFile(file, index, undefined, "LOGO");
                    }}
                  />

                  {item.logo && (
                    <img
                      src={item.logo}
                      alt="logo"
                      className="mt-2"
                      style={{
                        height: 90,
                        borderRadius: 6,
                        border: "1px solid #ddd",
                      }}
                    />
                  )}
                  {validationErrors[index]?.logo && (
                    <p className="text-danger small mt-1">
                      {validationErrors[index].logo}
                    </p>
                  )}
                </Form.Group>

                {/* IMAGES */}
                <Form.Label className="label mt-3">Images</Form.Label>

                {item.images.map((img: any, imgIndex: number) => (
                  <div key={imgIndex} className="mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <Form.Control
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const input = e.target as HTMLInputElement;
                          const file = input.files?.[0];

                          if (!file) return;

                          if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
                            const msg =
                              "Image format not supported. Please upload PNG, JPG, JPEG or SVG image.";

                            setFileError(msg);
                            toast.error(msg);
                            input.value = "";
                            return;
                          }
                          setFileError("");
                          if (file) uploadFile(file, index, imgIndex, "IMAGE");
                        }}
                      />

                      <PlusSquareFill
                        size={22}
                        className="cursor-pointer"
                        onClick={() => {
                          const updated = [...glimpses];
                          updated[index].images.push({ image: "" });
                          setGlimpses(updated);
                        }}
                      />

                      {item.images.length > 1 && (
                        <DashSquareFill
                          size={22}
                          className="cursor-pointer"
                          onClick={() => {
                            const updated = [...glimpses];
                            updated[index].images = updated[
                              index
                            ].images.filter(
                              (_: any, i: number) => i !== imgIndex
                            );
                            setGlimpses(updated);
                          }}
                        />
                      )}
                    </div>

                    {img.image && (
                      <img
                        src={img.image}
                        alt="preview"
                        className="mt-2"
                        style={{
                          height: 70,
                          borderRadius: 6,
                          border: "1px solid #ddd",
                        }}
                      />
                    )}
                  </div>
                ))}

                {/* ADD / REMOVE GLIMPSE */}
                <div className="d-flex gap-2 mt-3">
                  {mode === "add" && (
                    <PlusSquareFill
                      size={26}
                      className="cursor-pointer"
                      onClick={() =>
                        setGlimpses([...glimpses, createEmptyGlimpse()])
                      }
                    />
                  )}

                  {glimpses.length > 1 && (
                    <DashSquareFill
                      size={26}
                      className="cursor-pointer"
                      onClick={() =>
                        setGlimpses(glimpses.filter((_, i) => i !== index))
                      }
                    />
                  )}
                </div>
              </Card>
            ))}
          </Card>
        </Form>
      </Offcanvas.Body>

      <div className="p-3">
        <CustomButton
          label="Save"
          className="w-100"
          isLoading={isLoading}
          onClick={handleSave}
        />
      </div>
    </Offcanvas>
  );
};

export default AddPastGlimpse;
